/**
 * drawerProfile — what the Flow Optimizer drawer offers for one tracker.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Every per-tracker difference of the drawer is a field here, decided by one
 * exhaustive matchTracker — never by a string comparison in a component.
 * Tres Commas reuses the Voluum shell (its flows are Voluum-shaped) minus what
 * only Voluum has: its own AI optimization, the lander/entity APIs, the queue
 * and the campaign-tier tag overrides.
 */

import { matchTracker, trackerCan, type TrackerId } from '@trafficpulse/shared';

import { queueProviderOf } from '@/features/trackers/flow-optimizer/queue-provider';

export interface DrawerProfile {
  /** Binom has its own drawer; Voluum and Tres Commas share the Standard one. */
  shell: 'standard' | 'binom';
  /** Binom-only Campaign + Advanced tabs. */
  campaignTabs: boolean;
  /** Bank-backed queue: the Queue tab and the queue-only rule actions. */
  queue: boolean;
  /** Tags tab — needs campaign-tier tag overrides for this tracker. */
  tagsTab: boolean;
  /** The tracker's own AI optimization (Voluum): its cards and weight locks. */
  trackerAi: boolean;
  /** Save a dirty Standard draft to the tracker before a CUSTOM Apply. */
  preSaveRawDraft: boolean;
  /** Voluum lander editor, entity prefill (Voluum-only endpoints). */
  voluumEntityApis: boolean;
  /** Bank offer caps keyed by this tracker's offer ids. */
  offerCaps: boolean;
  /** Apply answers `applied: false` with a reason (its own switch / guard). */
  reportsUnpushed: boolean;
  /** Whose /flow-optimizer/metadata the editor reads (static payload). Binom
   *  has always read Voluum's — kept as is; Tres Commas reads its own. */
  metadataFrom: TrackerId;
}

const VOLUUM: Omit<DrawerProfile, 'queue'> = {
  shell: 'standard',
  campaignTabs: false,
  tagsTab: true,
  trackerAi: true,
  preSaveRawDraft: true,
  voluumEntityApis: true,
  offerCaps: true,
  reportsUnpushed: false,
  metadataFrom: 'voluum',
};

const BINOM: Omit<DrawerProfile, 'queue'> = {
  shell: 'binom',
  campaignTabs: true,
  tagsTab: true,
  trackerAi: false,
  preSaveRawDraft: false,
  voluumEntityApis: false,
  offerCaps: true,
  reportsUnpushed: false,
  metadataFrom: 'voluum',
};

const TRES_COMMAS: Omit<DrawerProfile, 'queue'> = {
  shell: 'standard',
  campaignTabs: false,
  tagsTab: false,
  trackerAi: false,
  preSaveRawDraft: true,
  voluumEntityApis: false,
  offerCaps: false,
  reportsUnpushed: true,
  metadataFrom: 'tres-commas',
};

export function drawerProfile(id: TrackerId): DrawerProfile {
  const base = matchTracker(id, {
    voluum: () => VOLUUM,
    binom: () => BINOM,
    'tres-commas': () => ({
      ...TRES_COMMAS,
      offerCaps: trackerCan(id, 'offerCaps'),
    }),
  });
  return { ...base, queue: queueProviderOf(id) !== null };
}

/**
 * Outside any drawer/tracker scope (isolated specs, a Voluum-only page): the
 * Standard editor as it has always rendered — every control on.
 */
export const UNSCOPED_PROFILE: DrawerProfile = { ...VOLUUM, queue: true };
